import { useEffect, useState } from "react";
import { Mail, Save, Send, CheckCircle2, AlertTriangle, Info, Sparkles, Users, ListChecks } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import BrandingSettings from "@/pages/BrandingSettings";
import TeamsSettings from "@/pages/TeamsSettings";
import FormBuilder from "@/pages/FormBuilder";

const PRESETS = {
  custom: { label: "Custom SMTP", host: "", port: 587 },
  gmail: { label: "Gmail", host: "smtp.gmail.com", port: 587 },
  outlook: { label: "Outlook / Microsoft 365", host: "smtp.office365.com", port: 587 },
  godaddy: { label: "GoDaddy Workspace", host: "smtpout.secureserver.net", port: 465 },
  zoho: { label: "Zoho", host: "smtp.zoho.com", port: 587 },
};

export default function Settings() {
  const { agent } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [preset, setPreset] = useState("gmail");
  const [form, setForm] = useState({
    enabled: false,
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    sender_email: "",
    sender_name: "InsureWell",
    use_tls: true,
  });
  const [passwordPlaceholder, setPasswordPlaceholder] = useState("");

  useEffect(() => {
    api.get("/settings/email")
      .then(({ data }) => {
        setForm((f) => ({
          ...f,
          enabled: data.enabled,
          smtp_host: data.smtp_host || "",
          smtp_port: data.smtp_port || 587,
          smtp_username: data.smtp_username || "",
          sender_email: data.sender_email || "",
          sender_name: data.sender_name || "InsureWell",
          use_tls: data.use_tls,
          smtp_password: "",
        }));
        if (data.smtp_password_set) {
          setPasswordPlaceholder("•••••••• (saved — leave blank to keep)");
        }
        // Detect preset
        const match = Object.entries(PRESETS).find(([_, v]) => v.host === data.smtp_host);
        if (match) setPreset(match[0]);
        else if (data.smtp_host) setPreset("custom");
      })
      .finally(() => setLoading(false));
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    const p = PRESETS[key];
    if (p && p.host) {
      setForm((f) => ({ ...f, smtp_host: p.host, smtp_port: p.port }));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings/email", form);
      toast.success("Email settings saved");
      const { data } = await api.get("/settings/email");
      if (data.smtp_password_set) {
        setPasswordPlaceholder("•••••••• (saved — leave blank to keep)");
        setForm((f) => ({ ...f, smtp_password: "" }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      // Save first so latest values are used
      await api.patch("/settings/email", form);
      const { data } = await api.post("/settings/email/test", { recipient: agent.email });
      if (data.success) {
        toast.success(`Test email sent to ${agent.email}`);
      } else {
        toast.error(data.message || "Test failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-10 text-sage" data-testid="settings-loading">Loading…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-4xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-clay font-semibold mb-2">Settings</div>
        <h1 className="font-heading text-3xl md:text-4xl tracking-tight font-medium">Customize InsureWell</h1>
        <p className="text-sage mt-2 text-sm">
          Branding, landing page content and email notifications — all in one place.
        </p>
      </div>

      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="bg-cream border border-line rounded-sm h-auto p-1 mb-6" data-testid="settings-tabs">
          <TabsTrigger value="branding" className="data-[state=active]:bg-forest data-[state=active]:text-bone rounded-sm px-4 py-2" data-testid="tab-branding">
            <Sparkles className="w-4 h-4 mr-2" /> Branding & landing
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-forest data-[state=active]:text-bone rounded-sm px-4 py-2" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" /> Email notifications
          </TabsTrigger>
          <TabsTrigger value="teams" className="data-[state=active]:bg-forest data-[state=active]:text-bone rounded-sm px-4 py-2" data-testid="tab-teams">
            <Users className="w-4 h-4 mr-2" /> Teams & routing
          </TabsTrigger>
          <TabsTrigger value="forms" className="data-[state=active]:bg-forest data-[state=active]:text-bone rounded-sm px-4 py-2" data-testid="tab-forms">
            <ListChecks className="w-4 h-4 mr-2" /> Form builder
          </TabsTrigger>
        </TabsList>

        <TabsContent value="branding" className="mt-0">
          <BrandingSettings />
        </TabsContent>

        <TabsContent value="teams" className="mt-0">
          <TeamsSettings />
        </TabsContent>

        <TabsContent value="forms" className="mt-0">
          <FormBuilder />
        </TabsContent>

        <TabsContent value="email" className="mt-0">
          <div className="mb-6">
            <h2 className="font-heading text-2xl tracking-tight font-medium">Email notifications</h2>
            <p className="text-sage mt-2 text-sm">
              Configure where new client submission alerts come from.
            </p>
          </div>

      {/* Status banner */}
      <div
        className={`mb-6 p-4 rounded-sm border flex items-start gap-3 ${
          form.enabled && form.smtp_host
            ? "bg-success/10 border-success/30"
            : "bg-cream border-line"
        }`}
        data-testid="settings-status-banner"
      >
        {form.enabled && form.smtp_host ? (
          <CheckCircle2 className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
        ) : (
          <Info className="w-5 h-5 text-clay flex-shrink-0 mt-0.5" />
        )}
        <div className="text-sm">
          <div className="font-medium text-forest">
            {form.enabled && form.smtp_host
              ? "Notifications enabled"
              : "Notifications are off"}
          </div>
          <div className="text-sage mt-1">
            {form.enabled && form.smtp_host
              ? `Outgoing mail will be routed via ${form.smtp_host}.`
              : "Add SMTP credentials below and toggle on. You can come back to this anytime."}
          </div>
        </div>
      </div>

      <div className="bg-white border border-line rounded-sm p-6 md:p-8 space-y-6">
        {/* Enable toggle */}
        <div className="flex items-center justify-between pb-6 border-b border-line">
          <div>
            <Label className="text-forest font-medium">Send email notifications</Label>
            <p className="text-sm text-sage mt-1">Master switch — turn off to silence all emails.</p>
          </div>
          <Switch
            checked={form.enabled}
            onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            data-testid="settings-enabled-switch"
          />
        </div>

        {/* Preset */}
        <div>
          <Label className="text-forest">Email provider</Label>
          <Select value={preset} onValueChange={applyPreset}>
            <SelectTrigger className="mt-1.5 h-11 border-line" data-testid="settings-preset-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PRESETS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-sage mt-2">
            We'll auto-fill the SMTP server &amp; port. You can override below if needed.
          </p>
        </div>

        {/* SMTP Host + Port */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label className="text-forest">SMTP server</Label>
            <Input
              value={form.smtp_host}
              onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
              placeholder="e.g. smtp.gmail.com"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="settings-host-input"
            />
          </div>
          <div>
            <Label className="text-forest">Port</Label>
            <Input
              type="number"
              value={form.smtp_port}
              onChange={(e) => setForm({ ...form, smtp_port: Number(e.target.value) || 587 })}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="settings-port-input"
            />
          </div>
        </div>

        {/* Username */}
        <div>
          <Label className="text-forest">Username (usually your email)</Label>
          <Input
            value={form.smtp_username}
            onChange={(e) => setForm({ ...form, smtp_username: e.target.value })}
            placeholder="you@example.com"
            className="mt-1.5 h-11 border-line input-clean"
            data-testid="settings-username-input"
          />
        </div>

        {/* Password */}
        <div>
          <Label className="text-forest">Password / App Password</Label>
          <Input
            type="password"
            value={form.smtp_password}
            onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
            placeholder={passwordPlaceholder || "App password (16 chars for Gmail/Outlook)"}
            className="mt-1.5 h-11 border-line input-clean"
            data-testid="settings-password-input"
          />
          <p className="text-xs text-sage mt-2 flex items-start gap-1.5">
            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            For Gmail/Outlook this is an <span className="font-medium">App Password</span>, not your regular login password.
            Get one at <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-clay hover:underline">myaccount.google.com/apppasswords</a> for Gmail.
          </p>
        </div>

        {/* Sender */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-forest">Sender email <span className="text-sage font-normal">(optional)</span></Label>
            <Input
              value={form.sender_email}
              onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
              placeholder="defaults to username"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="settings-sender-email-input"
            />
          </div>
          <div>
            <Label className="text-forest">Sender display name</Label>
            <Input
              value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              placeholder="InsureWell"
              className="mt-1.5 h-11 border-line input-clean"
              data-testid="settings-sender-name-input"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-line">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-forest hover:bg-forestdark text-bone rounded-sm h-11 px-6"
            data-testid="settings-save-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save settings"}
          </Button>
          <Button
            onClick={sendTest}
            disabled={testing || !form.smtp_host}
            variant="outline"
            className="border-clay text-clay hover:bg-clay hover:text-bone rounded-sm h-11 px-6"
            data-testid="settings-test-btn"
          >
            <Send className="w-4 h-4 mr-2" /> {testing ? "Sending…" : `Send test to ${agent?.email}`}
          </Button>
        </div>
      </div>

      {/* Help footnote */}
      <div className="mt-6 p-4 bg-cream border border-line rounded-sm flex items-start gap-3">
        <Mail className="w-4 h-4 text-clay flex-shrink-0 mt-0.5" />
        <div className="text-sm text-sage">
          <span className="text-forest font-medium">Don't have credentials yet?</span> No worries — leave this off
          for now and come back when you do. The app keeps working perfectly without email; you'll just check
          new submissions on the Clients page directly.
        </div>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
